/* global Blockly */
import React from 'react'
import { _defineHelpUrlandToolTip } from '../../helpers/index.js'

function defineBlock() {
    return {
        init: function () {
            let MODE =
                [
                    [Blockly.Msg['LISTS_SET_INDEX_SET'], 'SET'],
                    [Blockly.Msg['LISTS_SET_INDEX_INSERT'], 'INSERT']
                ];
            this.WHERE_OPTIONS =
                [
                    [Blockly.Msg['LISTS_GET_INDEX_FROM_START'], 'FROM_START'],
                    [Blockly.Msg['LISTS_GET_INDEX_FROM_END'], 'FROM_END'],
                    [Blockly.Msg['LISTS_GET_INDEX_FIRST'], 'FIRST'],
                    [Blockly.Msg['LISTS_GET_INDEX_LAST'], 'LAST'],
                    [Blockly.Msg['LISTS_GET_INDEX_RANDOM'], 'RANDOM']
                ];
            this.setHelpUrl(Blockly.Msg['LISTS_SET_INDEX_HELPURL']);
            this.setStyle('list_blocks');
            this.appendDummyInput()
                .appendField(Blockly.Msg['LISTS_SET_INDEX_INPUT_IN_LIST'])
                .appendField(new Blockly.FieldVariable(), "LIST");
            this.appendDummyInput()
                .appendField(new Blockly.FieldDropdown(MODE), 'MODE')
                .appendField('', 'SPACE');
            this.appendDummyInput('AT');
            this.appendValueInput('TO')
                .appendField(Blockly.Msg['LISTS_SET_INDEX_INPUT_TO']);
            this.setInputsInline(true);
            this.setPreviousStatement(true);
            this.setNextStatement(true);
            this.setHelpUrl(Blockly.BASE_HELP_URL + "#lists");
            this.setTooltip(Blockly.Msg['LISTS_SET_INDEX_TOOLTIP']);
            this.updateAt_(true);
            this.setTooltip(() => {
                let mode = this.getFieldValue('MODE');
                let where = this.getFieldValue('WHERE');
                let tooltip = '';
                switch (mode + ' ' + where) {
                    case 'SET FROM_START':
                    case 'SET FROM_END':
                        tooltip = Blockly.Msg['LISTS_SET_INDEX_TOOLTIP_SET_FROM'];
                        break;
                    case 'SET FIRST':
                        tooltip = Blockly.Msg['LISTS_SET_INDEX_TOOLTIP_SET_FIRST'];
                        break;
                    case 'SET LAST':
                        tooltip = Blockly.Msg['LISTS_SET_INDEX_TOOLTIP_SET_LAST'];
                        break;
                    case 'SET RANDOM':
                        tooltip = Blockly.Msg['LISTS_SET_INDEX_TOOLTIP_SET_RANDOM'];
                        break;
                    case 'INSERT FROM_START':
                    case 'INSERT FROM_END':
                        tooltip = Blockly.Msg['LISTS_SET_INDEX_TOOLTIP_INSERT_FROM'];
                        break;
                    case 'INSERT FIRST':
                        tooltip = Blockly.Msg['LISTS_SET_INDEX_TOOLTIP_INSERT_FIRST'];
                        break;
                    case 'INSERT LAST':
                        tooltip = Blockly.Msg['LISTS_SET_INDEX_TOOLTIP_INSERT_LAST'];
                        break;
                    case 'INSERT RANDOM':
                        tooltip = Blockly.Msg['LISTS_SET_INDEX_TOOLTIP_INSERT_RANDOM'];
                        break;
                }
                if (where === 'FROM_START' || where === 'FROM_END') {
                    tooltip += '  ' + Blockly.Msg['LISTS_INDEX_FROM_START_TOOLTIP']
                        .replace('%1',
                            this.workspace.options.oneBasedIndex ? '#1' : '#0');
                }
                return tooltip;
            });
        },
        /**
         * Create XML to represent whether there is an 'AT' input.
         * @return {Element} XML storage element.
         * @this Blockly.Block
         */
        mutationToDom: function () {
            let container = Blockly.utils.xml.createElement('mutation');
            let isAt = this.getInput('AT').type === Blockly.INPUT_VALUE;
            container.setAttribute('at', isAt);
            return null;
        },
        /**
         * Parse XML to restore the 'AT' input.
         * @param {!Element} xmlElement XML storage element.
         * @this Blockly.Block
         */
        domToMutation: function (xmlElement) {
            // Note: Until January 2013 this block did not have mutations,
            // so 'at' defaults to true.
            let isAt = (xmlElement.getAttribute('at') !== 'false');
            this.updateAt_(isAt);
        },
        /**
         * Create or delete an input for the numeric index.
         * @param {boolean} isAt True if the input should exist.
         * @private
         * @this Blockly.Block
         */
        updateAt_: function (isAt) {
            // Destroy old 'AT' and 'ORDINAL' input.
            this.removeInput('AT');
            this.removeInput('ORDINAL', true);
            // Create either a value 'AT' input or a dummy input.
            if (isAt) {
                let input = this.appendValueInput('AT').setCheck('Number');
                let block = this.workspace.newBlock("math_whole_number");
                block.setShadow(true);
                block.getField("NUM").setValue(1);
                input.connection.connect(block.outputConnection);
                if(this.rendered){
                    block.initSvg();
                    block.render();
                }

                if (Blockly.Msg['ORDINAL_NUMBER_SUFFIX']) {
                    this.appendDummyInput('ORDINAL')
                        .appendField(Blockly.Msg['ORDINAL_NUMBER_SUFFIX']);
                }
            } else {
                this.appendDummyInput('AT');
            }
            let menu = new Blockly.FieldDropdown(this.WHERE_OPTIONS, function (value) {
                let newAt = (value === 'FROM_START') || (value === 'FROM_END');
                // The 'isAt' variable is available due to this function being a closure.
                if (newAt !== isAt) {
                    let block = this.getSourceBlock();
                    block.updateAt_(newAt);
                    // This menu has been destroyed and replaced.  Update the replacement.
                    block.setFieldValue(value, 'WHERE');
                    return null;
                }
                return undefined;
            });
            this.moveInputBefore('AT', 'TO');
            if (this.getInput('ORDINAL')) {
                this.moveInputBefore('ORDINAL', 'TO');
            }

            this.getInput('AT').appendField(menu, 'WHERE');
            if (this.rendered) {
                this.initSvg();
                this.render();
            }
        }
    };

}

// Cache non-trivial values to variables to prevent repeated look-ups.
// `ctx.list` is mutated in place so callers observe the cached variable name.
function cacheList(ctx) {
    if (ctx.list.match(/^\w+$/)) {
        return '';
    }
    let listVar = Blockly.JavaScript.variableDB_.getDistinctName(
        'tmpList', Blockly.Variables.NAME_TYPE);
    let code = 'var ' + listVar + ' = ' + ctx.list + ';\n';
    ctx.list = listVar;
    return code;
}

function setIndexFirst(ctx) {
    if (ctx.mode === 'SET') {
        return ctx.list + '[0] = ' + ctx.value + ';\n';
    }
    if (ctx.mode === 'INSERT') {
        return ctx.list + '.unshift(' + ctx.value + ');\n';
    }
    return null;
}

function setIndexLast(ctx) {
    if (ctx.mode === 'SET') {
        let code = cacheList(ctx);
        code += ctx.list + '[' + ctx.list + '.length - 1] = ' + ctx.value + ';\n';
        return code;
    }
    if (ctx.mode === 'INSERT') {
        return ctx.list + '.push(' + ctx.value + ');\n';
    }
    return null;
}

function setIndexFromStart(ctx) {
    let at = Blockly.JavaScript.getAdjusted(ctx.block, 'AT');
    if (ctx.mode === 'SET') {
        return ctx.list + '[' + at + '] = ' + ctx.value + ';\n';
    }
    if (ctx.mode === 'INSERT') {
        return ctx.list + '.splice(' + at + ', 0, ' + ctx.value + ');\n';
    }
    return null;
}

function setIndexFromEnd(ctx) {
    let at = Blockly.JavaScript.getAdjusted(ctx.block, 'AT', 1, false,
        Blockly.JavaScript.ORDER_SUBTRACTION);
    let code = cacheList(ctx);
    if (ctx.mode === 'SET') {
        code += ctx.list + '[' + ctx.list + '.length - ' + at + '] = ' + ctx.value + ';\n';
        return code;
    }
    if (ctx.mode === 'INSERT') {
        code += ctx.list + '.splice(' + ctx.list + '.length - ' + at + ', 0, ' + ctx.value +
            ');\n';
        return code;
    }
    return null;
}

function setIndexRandom(ctx) {
    let code = cacheList(ctx);
    let xVar = Blockly.JavaScript.variableDB_.getDistinctName(
        'tmpX', Blockly.Variables.NAME_TYPE);
    code += 'var ' + xVar + ' = Math.floor(Math.random() * ' + ctx.list +
        '.length);\n';
    if (ctx.mode === 'SET') {
        code += ctx.list + '[' + xVar + '] = ' + ctx.value + ';\n';
        return code;
    }
    if (ctx.mode === 'INSERT') {
        code += ctx.list + '.splice(' + xVar + ', 0, ' + ctx.value + ');\n';
        return code;
    }
    return null;
}

// A Map, not an object literal: `where` comes from workspace XML, and an
// object literal would resolve inherited keys like `toString` to a function.
const SET_INDEX_HANDLERS = new Map([
    ['FIRST', setIndexFirst],
    ['LAST', setIndexLast],
    ['FROM_START', setIndexFromStart],
    ['FROM_END', setIndexFromEnd],
    ['RANDOM', setIndexRandom]
]);

function defineGenerators() {
    return {
        'JavaScript': function (block) {// Set element at index.
            // Note: Until February 2013 this block did not have MODE or WHERE inputs.
            // var list = Blockly.JavaScript.valueToCode(block, 'LIST',
            //     Blockly.JavaScript.ORDER_MEMBER) || '[]';
            let list = Blockly.JavaScript.variableDB_ ?
                Blockly.JavaScript.variableDB_.getName(block.getField('LIST').getText(), Blockly.Variables.NAME_TYPE) : [];

            let where = block.getFieldValue('WHERE') || 'FROM_START';
            let ctx = {
                block: block,
                list: list,
                mode: block.getFieldValue('MODE') || 'GET',
                value: Blockly.JavaScript.valueToCode(block, 'TO',
                    Blockly.JavaScript.ORDER_ASSIGNMENT) || 'null'
            };

            let handler = SET_INDEX_HANDLERS.get(where);
            let code = handler ? handler(ctx) : null;
            if (code == null) {
                throw new Error('Unhandled combination (lists_setIndex).');
            }
            return code;
        }
    }
}

const ListsSetIndex2 = function (props) {
    let name = "lists_setIndex_2";
    ListsSetIndex2.blockName = name;

    Blockly.Blocks[name] = defineBlock();

    let generators = defineGenerators();
    for (let i in generators) {
        Blockly[i][name] = generators[i];
    }

    _defineHelpUrlandToolTip(name,  Blockly.BASE_HELP_URL + "#lists");

    return (
        <block type={name}>
            <value name="AT">
                <shadow type="math_whole_number">
                    <field name="NUM">1</field>
                </shadow>
            </value>
        </block>
    );
}

export default ListsSetIndex2;
